const BRIDGE_URL = "ws://localhost:9900";
const CONNECT_TIMEOUT = 2000;

export class EdcError extends Error {
  constructor({ code, message, responseCode, recoverable }) {
    super(message);
    this.name = "EdcError";
    this.code = code;
    this.responseCode = responseCode || null;
    this.recoverable = recoverable || false;
  }
}

class HardwareBridgeService {
  constructor() {
    this.socket = null;
    this._connectPromise = null;
    this._responseHandlers = new Map(); // id → callback
    this._idCounter = 0;
  }

  get isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  connect() {
    if (this.isConnected) return Promise.resolve();
    if (this._connectPromise) return this._connectPromise;

    this._connectPromise = new Promise((resolve, reject) => {
      const ws = new WebSocket(BRIDGE_URL);
      const timeout = setTimeout(() => {
        ws.close();
        this._connectPromise = null;
        reject(new Error("Hardware Bridge connection timeout"));
      }, CONNECT_TIMEOUT);

      ws.onopen = () => {
        clearTimeout(timeout);
        this.socket = ws;
        this._connectPromise = null;
        resolve();
      };

      ws.onerror = () => {
        clearTimeout(timeout);
        ws.close();
        this._connectPromise = null;
        reject(new Error("Hardware Bridge unavailable"));
      };

      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        // Skip connected event
        if (data.event === "connected") return;

        // Route by id
        if (data.id && this._responseHandlers.has(data.id)) {
          this._responseHandlers.get(data.id)(data);
        }
      };

      ws.onclose = () => {
        this.socket = null;
        this._connectPromise = null;
        this._responseHandlers.clear();
      };
    });

    return this._connectPromise;
  }

  /**
   * Send a message and receive responses via callback.
   * Automatically generates an id for request/response matching.
   * Returns a function to unsubscribe the callback.
   */
  send(message, onResponse) {
    if (!this.isConnected) {
      throw new Error("Not connected to Hardware Bridge");
    }

    const id = `msg-${++this._idCounter}-${Date.now()}`;
    const msg = { id, ...message };

    if (onResponse) {
      this._responseHandlers.set(id, onResponse);
    }

    this.socket.send(JSON.stringify(msg));

    // Return unsubscribe function
    return () => {
      this._responseHandlers.delete(id);
    };
  }

  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this._responseHandlers.clear();
  }
}

export default new HardwareBridgeService();
